<script setup lang="ts">
import { computed, markRaw } from "vue";

import { useWindowSize } from "@/composables/useWindowSize";
import { useWorkerJobsEditor } from "@/modules/worker/composables/useWorkerJobsEditor";

import type { TableAltColumns, TableAltCustomElement, TableAltRows } from "@/dsComponents/TableAlt/types";
import type { WorkerDetail } from "@/modules/worker/types";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import ModalConfirm from "@/dsComponents/modals/ModalConfirm.vue";
import TableAlt from "@/dsComponents/TableAlt/TableAlt.vue";

import ProfileSection from "@/components/profile/section/ProfileSection.vue";
import WorkerJobEditModal from "@/modules/worker/components/workerJobs/WorkerJobEditModal.vue";

const props = withDefaults(
    defineProps<{
        worker: WorkerDetail;
        refreshWorker?: () => Promise<void>;
        readonly: boolean;
        asideLayout?: boolean;
    }>(),
    { asideLayout: false }
);

const {
    t,
    isEditJobModalOpen,
    isNewJob,
    isAlertDeleteModalOpen,
    isUpdateWorkerLoading,
    jobNameSelected,
    degreeSelected,
    experienceSelected,
    jobToUpdate,
    workerJobsFormatted,
    jobOptions,
    degreeOptions,
    experienceOptions,
    onClickEditJob,
    onClickAddNewJob,
    onClickDeleteJob,
    onClickCloseEditJobModal,
    deleteWorkerJobs,
    handleSave,
    handleJobSelect,
    handleJobUpdate,
} = useWorkerJobsEditor(props);

const { clientWidth } = useWindowSize();

const altColumns = computed<TableAltColumns[]>(() => {
    const cols: TableAltColumns[] = [{ field: "name", style: { minWidth: "160px" } }];
    if (clientWidth.value < 1600) {
        cols.push({ field: "degree" });
        cols.push({ field: "experience" });
    } else {
        cols.push({ field: "degree", width: "160px" });
        cols.push({ field: "experience", width: "160px" });
    }
    return cols;
});

const altRows = computed<TableAltRows[]>(() => {
    return workerJobsFormatted.value.map((job) => ({
        content: {
            name: { text: { value: job.name, leading: true } },
            degree: { text: { value: job.degreeLevelLabel ?? "", passive: true } },
            experience: { text: { value: job.experienceText ?? "", passive: true } },
        },
    }));
});

const globalActions = computed(() => ({
    width: "69px",
    rowActions: [
        {
            component: markRaw(ButtonAlt),
            props: { iconOnly: "edit", type: "neutral", variant: "ghost", size: "sm", title: t("global.actions.edit") },
            action: (rowIndex: number) => onClickEditJob(workerJobsFormatted.value[rowIndex].jobId),
        },
        {
            component: markRaw(ButtonAlt),
            props: { iconOnly: "trash", type: "error", variant: "ghost", size: "sm", title: t("global.actions.delete") },
            action: (rowIndex: number) => onClickDeleteJob(workerJobsFormatted.value[rowIndex].jobId),
        },
    ] as TableAltCustomElement<unknown>[],
}));
</script>
<template>
    <div class="worker-jobs">
        <ProfileSection
            :title="{ label: t('page.interim.profile.qualifications.title') }"
            :section-gap="asideLayout ? 'var(--spacing-xl, 16px)' : undefined"
        >
            <template v-if="!readonly" #actions>
                <ButtonAlt
                    v-if="asideLayout"
                    type="neutral"
                    variant="ghost"
                    icon-only="plus"
                    size="sm"
                    :title="t('global.actions.add')"
                    @click="onClickAddNewJob"
                />
                <ButtonAlt v-else type="neutral" variant="filled" left-icon="plus" size="sm" :label="t('global.actions.add')" @click="onClickAddNewJob" />
            </template>
            <template #content>
                <TableAlt
                    v-if="!asideLayout"
                    :columns="altColumns"
                    :rows="altRows"
                    :global-actions="readonly ? undefined : globalActions"
                    row-separators
                    always-show-row-action
                />
                <div v-else class="worker-jobs__aside-body">
                    <div
                        v-for="job in workerJobsFormatted"
                        :key="job.jobId"
                        class="worker-jobs__aside-row"
                        :class="{ 'worker-jobs__aside-row--actions': !readonly }"
                    >
                        <div class="worker-jobs__aside-row-main">
                            <span class="worker-jobs__aside-job typo-xxs-medium">{{ job.name }}</span>
                            <div class="worker-jobs__aside-experience">
                                <span v-if="job.experienceText" class="worker-jobs__aside-experience-text typo-xs-medium">
                                    {{ job.experienceText }}
                                </span>
                            </div>
                        </div>
                        <template v-if="!readonly">
                            <ButtonAlt
                                type="neutral"
                                variant="ghost"
                                icon-only="edit"
                                size="sm"
                                :title="t('global.actions.edit')"
                                @click="onClickEditJob(job.jobId)"
                            />
                            <ButtonAlt
                                type="error"
                                variant="ghost"
                                icon-only="trash"
                                size="sm"
                                :title="t('global.actions.delete')"
                                @click="onClickDeleteJob(job.jobId)"
                            />
                        </template>
                    </div>
                </div>
            </template>
        </ProfileSection>
        <WorkerJobEditModal
            v-model:job-name="jobNameSelected"
            v-model:degree="degreeSelected"
            v-model:experience="experienceSelected"
            :open="isEditJobModalOpen"
            :is-new-job="isNewJob"
            :loading="isUpdateWorkerLoading"
            :save-disabled="!jobToUpdate.jobId"
            :job-options="jobOptions"
            :degree-options="degreeOptions"
            :experience-options="experienceOptions"
            @close="onClickCloseEditJobModal"
            @save="handleSave"
            @select-job="handleJobSelect"
            @select-degree="(option) => handleJobUpdate('degreeLevel', option.value)"
            @select-experience="(option) => handleJobUpdate('experience', option.value)"
        />
        <ModalConfirm
            :open="isAlertDeleteModalOpen"
            :title="t('modals.removeWorkerJob')"
            :loading="isUpdateWorkerLoading"
            max-width="420px"
            @confirm="deleteWorkerJobs"
            @cancel="isAlertDeleteModalOpen = false"
        />
    </div>
</template>
<style scoped lang="scss">
.worker-jobs {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md, 8px);
    width: 100%;

    &__aside-body {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-xxs, 2px);
        width: 100%;
        min-width: 0;
        padding: var(--spacing-md, 8px) 0 var(--spacing-md, 8px) var(--spacing-md, 8px);
        box-sizing: border-box;
    }

    &__aside-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        align-items: center;
        min-height: 36px;
        column-gap: var(--spacing-xs, 4px);
        padding-left: var(--spacing-md, 8px);
        box-sizing: border-box;

        &--actions {
            grid-template-columns: minmax(0, 1fr) auto auto;
        }

        &:not(:last-child) {
            padding-bottom: var(--spacing-xxs, 2px);
        }

        &:not(:first-child) {
            padding-top: var(--spacing-xxs, 2px);
        }
    }

    &__aside-row-main {
        display: flex;
        flex: 1 1 0;
        align-items: center;
        min-width: 0;
        gap: var(--spacing-xs, 4px);
    }

    &__aside-job {
        flex: 1 1 0;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--text-main-900, #00061a);
    }

    &__aside-experience {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        width: 120px;
        min-height: 28px;
        padding: 0 var(--spacing-md, 8px);
        box-sizing: border-box;
    }

    &__aside-experience-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 100%;
        color: var(--text-main-900, #00061a);
    }
}
</style>
